import React, { useEffect, useRef, useState, useCallback } from "react";
import "./ProductReviews.scss";
import { Button, Input, Rate, Spin, Typography, notification } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router";
import dayjs from "dayjs";
import classNames from "classnames";
import {
  getItemReviews,
  submitReview,
  removeReview,
} from "../../../../features/reviews/reviewsActions";
import { resetReviewsError } from "../../../../features/reviews/reviewsSlice";
import { CustomEmpty } from "../../../../components";
import { ConfirmModal } from "../../../../modals";
import { ReactComponent as StarIcon } from "../../../../assets/startIcon.svg";
import Utils from "../../../../utils";

const { TextArea } = Input;

const PER_PAGE = 5;
const COMMENT_MAX_LENGTH = 1000;

// ?sort= chips: the value is what goes on the wire ("" = don't send the
// param — "Most recent" is the server's default order, not a sort value).
// best = 5★ → 1★, worst = 1★ → 5★, ties newest first either way; the own
// review stays pinned to row 1 of page 1 under every sort.
const SORT_OPTIONS = [
  { label: "Most recent", value: "" },
  { label: "Highest rated", value: "best" },
  { label: "Lowest rated", value: "worst" },
];

const DRAFT_LABELS = [
  "Tap to rate",
  "Poor",
  "Fair",
  "Good",
  "Very good",
  "Excellent",
];

export default function ProductReviews({ token, productId, hidden }) {
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const {
    itemReviewsLoading,
    itemReviewsMeta: meta,
    submitting,
    error,
  } = useSelector((state) => state?.reviews);
  // The customer's own review after submits/deletes this session — the
  // header stars write through the same slice, so a rating made up there
  // lands here too and triggers a list refetch below.
  const override = useSelector((state) => state?.reviews?.overrides)?.[
    productId
  ];
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);

  const rawSort = searchParams?.get("sort") || "";
  // Bad values (?sort=banana) fall back to newest-first server-side; the
  // chips mirror that by keeping "Most recent" highlighted.
  const sort = SORT_OPTIONS?.some((option) => option?.value === rawSort)
    ? rawSort
    : "";

  // The design paginates with "Load more", so pages accumulate here instead
  // of living in the slice (which keeps only the latest page).
  const [reviewsList, setReviewsList] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // Bumped whenever the list is rebuilt from page 1 — a load-more response
  // that lands after a sort/product change must not append to the new list.
  const requestIdRef = useRef(0);

  const [isEditing, setIsEditing] = useState(false);
  const [draftRating, setDraftRating] = useState(0);
  const [draftComment, setDraftComment] = useState("");
  const [hoverStar, setHoverStar] = useState(0);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);

  const resetDrafts = () => {
    setIsEditing(false);
    setDraftRating(0);
    setDraftComment("");
    setHoverStar(0);
  };

  useEffect(() => {
    if (!productId) return;
    const requestId = ++requestIdRef.current;
    setPage(1);
    // Every submit/delete changes `override`, so the list (and the count in
    // the Reviews tab) refetches itself after each write.
    dispatch(
      getItemReviews({
        menuItemId: productId,
        page: 1,
        per_page: PER_PAGE,
        sort: sort ? sort : undefined,
      })
    )?.then((res) => {
      if (requestIdRef.current !== requestId) return;
      if (res?.type === "reviews/get_item_reviews/fulfilled") {
        setReviewsList(res?.payload?.results || []);
      }
    });
  }, [dispatch, productId, sort, override]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    dispatch(
      getItemReviews({
        menuItemId: productId,
        page: nextPage,
        per_page: PER_PAGE,
        sort: sort ? sort : undefined,
      })
    )?.then((res) => {
      setLoadingMore(false);
      if (requestIdRef.current !== requestId) return;
      if (res?.type === "reviews/get_item_reviews/fulfilled") {
        setPage(nextPage);
        setReviewsList((prev) =>
          // The pinned own review could reappear at its natural rank on a
          // later page — dedupe by id keeps it single.
          Utils?.madeUnique([...prev, ...(res?.payload?.results || [])])
        );
      }
    });
  };

  const onSortChange = (value) => {
    if (value === sort) return;
    // Sort lives in the URL like the MyReviews filters, so a reload or a
    // shared link restores it; "Most recent" just drops the param. The page
    // is not in the URL — the effect above always restarts from page 1.
    const filteredSearchParams = Utils?.get_filtered_url_params(searchParams, {
      sort: value ? value : undefined,
    });
    setSearchParams(filteredSearchParams, { replace: true });
  };

  const handleSubmit = () => {
    if (!draftRating || submitting) return;
    // The upsert overwrites the customer's whole review of this item.
    dispatch(
      submitReview({
        menuItemId: productId,
        rating: draftRating,
        comment: draftComment,
      })
    )?.then((res) => {
      if (res?.type === "reviews/submit_item/fulfilled") {
        resetDrafts();
      }
    });
  };

  const handleDelete = () => {
    dispatch(removeReview(productId))?.then((res) => {
      if (res?.type === "reviews/remove_item/fulfilled") {
        resetDrafts();
      }
    });
  };

  useEffect(() => {
    if (location?.pathname?.includes("product")) {
      if (error && !itemReviewsLoading && !submitting) {
        for (let key in error) {
          openNotificationWithIcon("error", key, error[key]);
        }
        dispatch(resetReviewsError());
      }
    }
  }, [
    error,
    itemReviewsLoading,
    submitting,
    location?.pathname,
    openNotificationWithIcon,
    dispatch,
  ]);

  const myReview = reviewsList?.find((review) => review?.is_mine);
  // The override wins when present: right after a submit the list hasn't
  // refetched yet, and without this the composer would flash back in.
  const hasOwnReview = override
    ? Boolean(override?.has_review)
    : Boolean(myReview);
  const showComposer = isAuthenticated && !hasOwnReview;
  const shownDraftLabel = DRAFT_LABELS[hoverStar || draftRating];
  const listLoading = itemReviewsLoading && !loadingMore;
  const hasMore = Boolean(meta?.count) && reviewsList?.length < meta?.count;
  // How many reviews the next click will actually fetch — a full page, or
  // whatever is left on the last one.
  const nextBatchCount = hasMore
    ? Math.min(meta?.count - reviewsList?.length, PER_PAGE)
    : 0;

  const startEditing = () => {
    setIsEditing(true);
    setDraftRating(myReview?.rating || 0);
    setDraftComment(myReview?.comment || "");
    setHoverStar(0);
  };

  return (
    <div
      className={classNames("productReviews-cont", {
        "productReviews-cont--hidden": hidden,
      })}>
      {contextHolder}
      {showComposer ? (
        <div
          className="productReviews-cont__composer"
          style={{
            backgroundColor: token?.Card?.colorBgCard,
          }}>
          <div className="productReviews-cont__composer__topRow">
            <Typography.Text
              className="productReviews-cont__composer__topRow__title"
              style={{
                color: token?.Typography?.mainColorText,
              }}>
              Rate this product
            </Typography.Text>
            <div className="productReviews-cont__composer__topRow__rateWrapper">
              <Rate
                // Input is whole stars only — halves exist only in
                // displayed averages.
                value={draftRating}
                disabled={submitting}
                character={<StarIcon width={22} height={22} />}
                count={5}
                onChange={(val) => {
                  setDraftRating(val);
                }}
                onHoverChange={(val) => {
                  setHoverStar(val || 0);
                }}
                style={{
                  fontSize: "16px",
                }}
              />
              {/* Always rendered, like the editor row — a label that mounts
                  and unmounts on hover makes the whole row reflow and the
                  stars visibly jump while the pointer travels over them. */}
              <Typography.Text className="productReviews-cont__composer__topRow__rateWrapper__label">
                {shownDraftLabel}
              </Typography.Text>
            </div>
          </div>
          <TextArea
            className="productReviews-cont__composer__textArea"
            placeholder="Type your comment"
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={COMMENT_MAX_LENGTH}
            value={draftComment}
            onChange={(e) => {
              setDraftComment(e?.target?.value);
            }}
          />
          <div className="productReviews-cont__composer__btnRow">
            {/* "Save", not "Add comment" — a rating alone is submittable,
                no comment required. */}
            <Button
              type="primary"
              size="small"
              loading={submitting}
              disabled={!draftRating}
              onClick={handleSubmit}>
              Save
            </Button>
            <span className="productReviews-cont__composer__btnRow__counter">
              {draftComment?.length}/{COMMENT_MAX_LENGTH}
            </span>
          </div>
        </div>
      ) : null}

      <div
        className="productReviews-cont__sortRow"
        role="group"
        aria-label="Sort reviews">
        <Typography.Text className="productReviews-cont__sortRow__label">
          Sort
        </Typography.Text>
        {SORT_OPTIONS?.map((option) => (
          <div
            key={option?.label}
            role="button"
            tabIndex={0}
            className={classNames("productReviews-cont__sortRow__chip", {
              "productReviews-cont__sortRow__chip--active":
                sort === option?.value,
            })}
            onClick={() => {
              onSortChange(option?.value);
            }}
            onKeyDown={(e) => {
              if (e?.key === "Enter" || e?.key === " ") {
                e?.preventDefault();
                onSortChange(option?.value);
              }
            }}>
            {option?.label}
          </div>
        ))}
      </div>

      <Spin
        size="large"
        spinning={listLoading}
        classNames={{
          root: classNames({
            "productReviews-cont__rows__spinWrapper":
              listLoading || !reviewsList?.length,
          }),
        }}>
        <div className="productReviews-cont__rows">
          {!reviewsList?.length && !listLoading ? (
            <CustomEmpty
              description="No reviews yet"
              circleColor={
                token?.icons?.emptyCircleIconColor
                  ? token?.icons?.emptyCircleIconColor
                  : ""
              }
              boxBorderColor={
                token?.icons?.emptyIconBoxBorderColor
                  ? token?.icons?.emptyIconBoxBorderColor
                  : ""
              }
            />
          ) : (
            reviewsList?.map((review) => {
              const isMine = Boolean(review?.is_mine);
              const isEditingThis = isMine && isEditing;
              return (
                <div
                  key={review?.id}
                  className={classNames("productReviews-cont__rows__row", {
                    "productReviews-cont__rows__row--mine": isMine,
                  })}
                  style={{
                    backgroundColor: token?.Card?.colorBgCard,
                  }}>
                  <div className="productReviews-cont__rows__row__nameRow">
                    <Typography.Text
                      className="productReviews-cont__rows__row__nameRow__name"
                      style={{
                        color: token?.Typography?.mainColorText,
                      }}>
                      {isMine ? "You" : review?.reviewer_name || ""}
                    </Typography.Text>
                    {isMine ? (
                      <span
                        className="productReviews-cont__rows__row__nameRow__badge"
                        style={{
                          color: token?.Typography?.colorPrimaryText,
                        }}>
                        Your review
                      </span>
                    ) : null}
                    <Rate
                      disabled
                      value={review?.rating ? review?.rating : 0}
                      character={<StarIcon width={13} height={13} />}
                      count={5}
                      style={{
                        fontSize: "11px",
                      }}
                    />
                    <Typography.Text className="productReviews-cont__rows__row__nameRow__date">
                      {review?.created_at
                        ? dayjs(review?.created_at)?.format("MMM D, YYYY")
                        : ""}
                    </Typography.Text>
                  </div>

                  {!isEditingThis ? (
                    <>
                      {review?.comment ? (
                        <Typography.Text
                          className="productReviews-cont__rows__row__comment"
                          style={{
                            color:
                              "color-mix(in srgb, var(--text-main, #fff) 85%, transparent)",
                          }}>
                          {review?.comment}
                        </Typography.Text>
                      ) : (
                        <div className="productReviews-cont__rows__row__noComment">
                          Rated {review?.rating} stars — no comment left.
                        </div>
                      )}
                      {isMine ? (
                        <div className="productReviews-cont__rows__row__actions">
                          <Button
                            size="small"
                            type="text"
                            aria-label="Edit review"
                            disabled={submitting}
                            icon={
                              <EditOutlined
                                style={{
                                  color: token?.Typography?.colorPrimaryText,
                                }}
                              />
                            }
                            onClick={startEditing}
                          />
                          <Button
                            size="small"
                            type="text"
                            aria-label="Delete review"
                            loading={
                              submitting && {
                                // Brand-colored spinner, same as the heart
                                // button's inline loading.
                                icon: (
                                  <LoadingOutlined
                                    spin
                                    style={{
                                      color: token?.icons?.colorPrimaryIcon,
                                    }}
                                  />
                                ),
                              }
                            }
                            icon={
                              <DeleteOutlined
                                style={{
                                  color: token?.colorError,
                                }}
                              />
                            }
                            onClick={() => {
                              setIsDeleteModalOpened(true);
                            }}
                          />
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="productReviews-cont__rows__row__editor">
                      <div className="productReviews-cont__rows__row__editor__rateRow">
                        <Rate
                          value={draftRating}
                          disabled={submitting}
                          character={<StarIcon width={22} height={22} />}
                          count={5}
                          onChange={(val) => {
                            setDraftRating(val);
                          }}
                          onHoverChange={(val) => {
                            setHoverStar(val || 0);
                          }}
                          style={{
                            fontSize: "16px",
                          }}
                        />
                        <Typography.Text className="productReviews-cont__rows__row__editor__rateRow__label">
                          {shownDraftLabel}
                        </Typography.Text>
                      </div>
                      <TextArea
                        className="productReviews-cont__rows__row__editor__textArea"
                        placeholder="Type your comment"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        maxLength={COMMENT_MAX_LENGTH}
                        value={draftComment}
                        onChange={(e) => {
                          setDraftComment(e?.target?.value);
                        }}
                      />
                      <div className="productReviews-cont__rows__row__editor__btnRow">
                        <Button
                          type="primary"
                          size="small"
                          loading={submitting}
                          disabled={!draftRating}
                          onClick={handleSubmit}>
                          Save changes
                        </Button>
                        <Button
                          size="small"
                          disabled={submitting}
                          onClick={resetDrafts}>
                          Cancel
                        </Button>
                        <span className="productReviews-cont__rows__row__editor__btnRow__counter">
                          {draftComment?.length}/{COMMENT_MAX_LENGTH}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Spin>

      {hasMore && !listLoading ? (
        <Button
          className="productReviews-cont__loadMoreBtn"
          loading={loadingMore}
          onClick={handleLoadMore}>
          Load {nextBatchCount} more {nextBatchCount === 1 ? "review" : "reviews"}
        </Button>
      ) : null}

      <ConfirmModal
        isOpened={isDeleteModalOpened}
        title={"Are you sure"}
        subTitle={"You want to delete this review ?"}
        onCancel={() => {
          setIsDeleteModalOpened(false);
        }}
        onOk={() => {
          handleDelete();
          setIsDeleteModalOpened(false);
        }}
        token={token}
      />
    </div>
  );
}
